/**
 * The rules behind the welcome flow — pure, so a node probe can check them
 * without a browser (`npm run test:onboarding`).
 *
 * The flow asks for five things and no more. The list is short on purpose:
 * the profile audit (docs/PROFILE_UX_PASS_2026-09-06.md) found that of ~50
 * settings, a handful actually change what the app does, and the page had
 * never been filled in by anybody. Asking for less, once, at the moment it
 * pays off, is the whole idea — so a question earns its place here only if a
 * running service reads the answer.
 */

export type StepId = 'name' | 'place' | 'day' | 'load' | 'first'

/**
 * What a person can say at the door, before any question is asked.
 *
 * The first version of this flow had no door: first login redirected straight
 * into "What should I call you?" with Skip and Next. Owner, on being onboarded
 * by it (2026-09-07): *"Kite did not onboard me well. I should be asked if I
 * want to be asked later."* Being asked is the point — a setup flow that
 * cannot be postponed is not an invitation, it is a toll gate.
 */
export type DoorChoice = 'start' | 'later' | 'never'

export interface DoorOption {
  choice: DoorChoice
  label: string
  /** What actually happens, said plainly. No option may be vague about this. */
  detail: string
}

export const DOOR_OPTIONS: DoorOption[] = [
  {
    choice: 'start',
    label: 'Set me up',
    detail: 'Five short questions. About a minute.',
  },
  {
    choice: 'later',
    label: 'Not now',
    detail: 'I will ask again next time you sign in.',
  },
  {
    choice: 'never',
    label: "Don't ask again",
    detail: 'You can still do it any time from Settings.',
  },
]

export interface Step {
  id: StepId
  /** What the person is asked. A question, not a field label. */
  question: string
  /** One line saying what the answer changes. Never a feature tour. */
  because: string
}

export const STEPS: Step[] = [
  {
    id: 'name',
    question: 'What should I call you?',
    because: 'It is how you will be spoken to, everywhere.',
  },
  {
    id: 'place',
    question: 'Where are you?',
    because: 'Your timezone decides what "today" and "7:00" mean in every reminder.',
  },
  {
    id: 'day',
    question: 'What does a normal day look like?',
    because: 'Nothing gets suggested outside these hours.',
  },
  {
    id: 'load',
    question: 'How much is a full day for you?',
    because: 'This is the most that will ever be asked of you at once.',
  },
  {
    id: 'first',
    question: 'What is one thing you want to be true a year from now?',
    because: 'We will keep it in view and break it down together.',
  },
]

export const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
export type DayKey = (typeof DAY_KEYS)[number]

export const DAY_LABELS: Record<DayKey, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
}

export interface Answers {
  name: string
  timezone: string
  wake: string
  sleep: string
  workStart: string
  workEnd: string
  workDays: DayKey[]
  perDay: number
  focusBlock: number
  dream: string
  firstStep: string
}

/** The browser's own zone, which is right far more often than a default is. */
export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export function initialAnswers(tz = detectTimezone()): Answers {
  return {
    name: '',
    timezone: tz,
    wake: '07:00',
    sleep: '23:00',
    workStart: '09:00',
    workEnd: '17:00',
    workDays: ['mon', 'tue', 'wed', 'thu', 'fri'],
    perDay: 3,
    focusBlock: 45,
    dream: '',
    firstStep: '',
  }
}

/**
 * Every step can be skipped. Nothing here is required, because a person who
 * cannot answer "what do you want to be true in a year" on their first day
 * should still reach the app — they can answer it later, or never.
 */
export function canContinue(): boolean {
  return true
}

/** Minutes between two HH:MM clock times, rolling past midnight. */
export function minutesBetween(from: string, to: string): number {
  const m = (s: string) => {
    const [h, mi] = s.split(':').map(Number)
    return (h || 0) * 60 + (mi || 0)
  }
  const d = m(to) - m(from)
  return d >= 0 ? d : d + 24 * 60
}

/**
 * What is obviously wrong with a day shape, in the person's words. Returns
 * null when the shape is usable. These are *notes*, never blocks: a night
 * shift is a real life, not a validation error.
 */
export function dayShapeNote(a: Answers): string | null {
  const awake = minutesBetween(a.wake, a.sleep)
  if (awake < 6 * 60) return 'That is a short day awake — worth a second look.'
  const work = minutesBetween(a.workStart, a.workEnd)
  if (work === 0) return 'Your working hours start and end at the same time.'
  if (work > 16 * 60) return 'That is a very long working day — worth a second look.'
  if (a.workDays.length === 0) return 'No working days picked, so the whole week is yours.'
  return null
}

/** One sentence describing the day just described, read back to the person. */
export function dayShapeSummary(a: Answers): string {
  if (a.workDays.length === 0) {
    return `Awake ${a.wake}–${a.sleep}, with no fixed working days.`
  }
  const days =
    a.workDays.length === 7 ? 'every day'
    : a.workDays.length === 5 && !a.workDays.includes('sat') && !a.workDays.includes('sun')
      ? 'weekdays'
      : a.workDays.map(d => DAY_LABELS[d]).join(', ')
  return `Awake ${a.wake}–${a.sleep}, working ${a.workStart}–${a.workEnd} on ${days}.`
}

/** How the load answer reads back, in things rather than numbers. */
export function loadSummary(a: Answers): string {
  const n = a.perDay
  const thing = n === 1 ? 'one thing' : `${n} things`
  return `At most ${thing} a day, in blocks of about ${a.focusBlock} minutes.`
}

/**
 * The scheduling-preferences payload. Only keys a live service reads — the
 * same set the Settings page now writes.
 */
export function schedulingPayload(a: Answers): Record<string, unknown> {
  return {
    wake_time: a.wake,
    sleep_time: a.sleep,
    work_start_time: a.workStart,
    work_end_time: a.workEnd,
    work_days: a.workDays,
    focus_duration_minutes: a.focusBlock,
    daily_focus_limit: a.perDay,
    daily_hold_limit: a.perDay,
  }
}

/** The personal-info payload. `display_name` is what the coach calls you. */
export function personalPayload(a: Answers): Record<string, unknown> {
  return { display_name: a.name.trim(), timezone: a.timezone }
}

/** Which steps produced something worth saving. Drives the closing summary. */
export function answeredSteps(a: Answers, start: Answers): StepId[] {
  const out: StepId[] = []
  if (a.name.trim()) out.push('name')
  if (a.timezone && a.timezone !== 'UTC') out.push('place')
  if (a.wake !== start.wake || a.sleep !== start.sleep ||
      a.workStart !== start.workStart || a.workEnd !== start.workEnd ||
      a.workDays.join() !== start.workDays.join()) out.push('day')
  if (a.perDay !== start.perDay || a.focusBlock !== start.focusBlock) out.push('load')
  if (a.dream.trim()) out.push('first')
  return out
}


/**
 * What each door choice must persist.
 *
 * `later` writes NOTHING on purpose: first-run already keeps a per-session
 * flag so nobody is asked twice in one sitting, and an empty `answered` list
 * next session brings the invitation back by itself. Storing a snooze date
 * would be machinery for a behaviour we already have for free — and a date is
 * a promise about WHEN, which "not now" does not make.
 */
export function doorPayload(choice: DoorChoice): Record<string, unknown> | null {
  return choice === 'never'
    ? { welcome_declined: true, welcome_pending: false }
    : null
}

/** Whether a choice sends the person on to the questions. */
export function opensTheFlow(choice: DoorChoice): boolean {
  return choice === 'start'
}


/**
 * What to read back at the end, in the order it was asked.
 *
 * Only what the person actually settled — a closing panel that lists things
 * they skipped would be the same lie as a form rendering its defaults as
 * answers. An empty list is a real outcome and the panel says so.
 */
export function closingLines(a: Answers, start: Answers): string[] {
  const done = answeredSteps(a, start)
  const out: string[] = []
  if (done.includes('name')) out.push(`I will call you ${a.name.trim()}.`)
  if (done.includes('place')) out.push(`Your times are ${a.timezone.replace(/_/g, ' ')}.`)
  if (done.includes('day')) out.push(dayShapeSummary(a))
  if (done.includes('load')) out.push(loadSummary(a))
  if (done.includes('first')) out.push(`Your first dream: “${a.dream.trim()}”.`)
  return out
}

/**
 * Steps the person DELIBERATELY answered.
 *
 * `answeredSteps` counts the timezone, because a detected zone really is set
 * and worth reading back. But it is not something they told us, so it must not
 * make the closing panel say "that's you set up" to someone who pressed Skip
 * five times — the first version did exactly that, and also thanked them for
 * hours they had never given.
 */
export function deliberateSteps(a: Answers, start: Answers): StepId[] {
  return answeredSteps(a, start).filter(
    (s) => s !== 'place' || a.timezone !== start.timezone,
  )
}

/**
 * The one line under the closing heading. It has to be honest about the two
 * very different states a person can arrive in — having answered nothing is
 * not a failure, and must not be congratulated as though it were.
 */
export function closingBlurb(count: number): string {
  if (count === 0) {
    return 'You skipped through, which is completely fine. I will work from sensible defaults, and Settings is there whenever you want to change them.'
  }
  return 'That is everything I needed. You can change any of it later in Settings.'
}

/** The closing heading, which must not congratulate an empty answer. */
export function closingTitle(deliberate: number): string {
  return deliberate === 0 ? 'All set for now.' : "That's you set up."
}

/**
 * What happens next. Never claims hours the person did not give: someone who
 * skipped the day question has not "given" any, and saying so would be the
 * flow's very first lie to them.
 */
export function whatHappensNext(
  a: Answers,
  start: Answers,
  assistant: string,
): string {
  if (a.dream.trim()) {
    return `Next, ${assistant} will help you break \u201C${a.dream.trim()}\u201D into goals you can start on. You will find it under Dreams.`
  }
  const gaveHours = deliberateSteps(a, start).includes('day')
  if (gaveHours) {
    return `${assistant} will suggest times inside the hours you gave, and never more than your daily limit.`
  }
  return `${assistant} will start from sensible defaults \u2014 a 9-to-5 weekday, three things a day. Change any of it on Your day in Settings.`
}
